import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient, useMutation } from "@apollo/client";
import dayjs, { type Dayjs } from "utils/dayjs";
import {
  Button,
  Card,
  CardContent,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Checkbox,
  DatePicker,
  Drawer,
  FormField,
  Select,
  TextField,
  toast,
} from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import { MERCHANT_NAME_SEARCH } from "graphql/card";
import {
  SAVE_PLATFORM_REWARD_RULE,
  SEARCH_PLATFORM_COMPANIES,
  SET_PLATFORM_REWARD_RULE_ENABLED,
} from "graphql/platform";
import { useSelectSearch } from "hooks/useSelectSearch";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { ruleSummary } from "utils/rewards/rewardRules";
import type {
  PlatformRewardRule,
  PlatformRewardRuleInput,
  RewardCategory,
  RewardRuleScope,
} from "./types";

const DAY_FORMAT = "YYYY-MM-DD";
const ALL_COMPANIES = "ALL";
const SELECTED_COMPANIES = "SELECTED";

interface RewardRuleDrawerProps {
  open: boolean;
  onClose: () => void;
  rule: PlatformRewardRule | null;
  rules: PlatformRewardRule[];
  categories: RewardCategory[];
  currency: string;
  canEdit: boolean;
  onSaved: () => void;
}

interface Form {
  name: string;
  scope: RewardRuleScope;
  defaultCategoryId: string | null;
  merchant: SelectOption | null;
  companiesMode: string;
  companies: SelectOption[];
  ratePercent: string;
  priority: string;
  limited: boolean;
  startsOn: Dayjs | null;
  endsOn: Dayjs | null;
  capped: boolean;
  monthlyCap: string;
}

function toForm(rule: PlatformRewardRule | null): Form {
  return {
    name: rule?.name ?? "",
    scope: rule?.scope ?? "ALL",
    defaultCategoryId: rule?.defaultCategoryId ?? null,
    merchant:
      rule?.merchantId && rule.merchantName
        ? { id: rule.merchantId, label: rule.merchantName }
        : null,
    companiesMode: rule?.companies.length ? SELECTED_COMPANIES : ALL_COMPANIES,
    companies: (rule?.companies ?? []).map((company) => ({
      id: company.id,
      label: company.name ?? company.id,
    })),
    ratePercent: rule ? String(rule.ratePercent) : "",
    priority: rule ? String(rule.priority) : "",
    limited: Boolean(rule?.startsOn),
    startsOn: rule?.startsOn ? dayjs.utc(rule.startsOn) : null,
    endsOn: rule?.endsOn ? dayjs.utc(rule.endsOn) : null,
    capped: Boolean(rule?.monthlyCap),
    monthlyCap: rule?.monthlyCap ? String(rule.monthlyCap) : "",
  };
}

const RewardRuleDrawer: React.FC<RewardRuleDrawerProps> = ({
  open,
  onClose,
  rule,
  rules,
  categories,
  currency,
  canEdit,
  onSaved,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [form, setForm] = useState<Form>(() => toForm(rule));
  const [saveRule, { loading: saving }] = useMutation(
    SAVE_PLATFORM_REWARD_RULE,
  );
  const [setEnabled, { loading: toggling }] = useMutation(
    SET_PLATFORM_REWARD_RULE_ENABLED,
  );

  useEffect(() => {
    if (open) setForm(toForm(rule));
  }, [open, rule]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const merchants = useSelectSearch<SelectOption>({
    seed: form.merchant,
    skip: !open || form.scope !== "MERCHANT",
    fetchPage: async (query) => {
      if (!query.trim()) return { options: [] };
      const { data } = await client.query({
        query: MERCHANT_NAME_SEARCH,
        variables: { searchValue: query, searchType: "NAME" },
      });
      return {
        options: (data?.merchantNameSearch ?? []).map(
          (merchant: { id: string; name: string }) => ({
            id: merchant.id,
            label: merchant.name,
          }),
        ),
      };
    },
  });

  const companies = useSelectSearch<SelectOption>({
    skip: !open || form.companiesMode !== SELECTED_COMPANIES,
    fetchPage: async (query, after) => {
      const { data } = await client.query({
        query: SEARCH_PLATFORM_COMPANIES,
        variables: {
          searchTerm: query || undefined,
          first: DEFAULT_PAGINATION_SIZE,
          after,
        },
      });
      const page = data?.getCompanies;
      return {
        options: (page?.edges ?? []).map(
          (edge: { node: { id: string; businessLegalName: string } }) => ({
            id: edge.node.id,
            label: edge.node.businessLegalName,
          }),
        ),
        hasNextPage: page?.pageInfo?.hasNextPage,
        endCursor: page?.pageInfo?.endCursor,
      };
    },
  });

  const companyOptions = useMemo(() => {
    const byId = new Map(
      companies.options.map((option) => [option.id, option]),
    );
    for (const option of form.companies) byId.set(option.id, option);
    return [...byId.values()];
  }, [companies.options, form.companies]);

  const rate = Number(form.ratePercent);
  const priority = Number(form.priority);
  const cap = Number(form.monthlyCap);
  const capCurrency = rule?.capCurrency ?? currency;
  const priorityHolder = rules.find(
    (each) => each.priority === priority && each.id !== rule?.id,
  );
  const complete =
    form.name.trim() !== "" &&
    rate > 0 &&
    Number.isInteger(priority) &&
    priority > 0 &&
    !priorityHolder &&
    (form.scope !== "CATEGORY" || Boolean(form.defaultCategoryId)) &&
    (form.scope !== "MERCHANT" || Boolean(form.merchant)) &&
    (form.companiesMode === ALL_COMPANIES || form.companies.length > 0) &&
    (!form.limited || Boolean(form.startsOn && form.endsOn)) &&
    (!form.capped || cap > 0);

  const categoryName =
    categories.find((category) => category.id === form.defaultCategoryId)
      ?.name ?? null;
  const summary = ruleSummary(
    {
      scope: form.scope,
      categoryName,
      merchantName: form.merchant?.label?.toString() ?? null,
      companies:
        form.companiesMode === SELECTED_COMPANIES
          ? form.companies.map((company) => ({
              id: company.id,
              name: company.label?.toString() ?? null,
            }))
          : [],
      ratePercent: rate || 0,
      startsOn: form.limited
        ? (form.startsOn?.format(DAY_FORMAT) ?? null)
        : null,
      endsOn: form.limited ? (form.endsOn?.format(DAY_FORMAT) ?? null) : null,
      monthlyCap: form.capped && cap > 0 ? cap : null,
      capCurrency,
    },
    t,
  );

  const handleSave = async () => {
    const input: PlatformRewardRuleInput = {
      name: form.name.trim(),
      scope: form.scope,
      ratePercent: rate,
      priority,
      ...(form.scope === "CATEGORY" &&
        form.defaultCategoryId && {
          defaultCategoryId: form.defaultCategoryId,
        }),
      ...(form.scope === "MERCHANT" &&
        form.merchant && {
          merchantId: form.merchant.id,
          merchantName: form.merchant.label?.toString(),
        }),
      ...(form.companiesMode === SELECTED_COMPANIES && {
        companyIds: form.companies.map((company) => company.id),
      }),
      ...(form.limited &&
        form.startsOn &&
        form.endsOn && {
          startsOn: form.startsOn.format(DAY_FORMAT),
          endsOn: form.endsOn.format(DAY_FORMAT),
        }),
      ...(form.capped && { monthlyCap: cap, capCurrency }),
      enabled: rule?.enabled ?? true,
    };
    try {
      await saveRule({ variables: { id: rule?.id, input } });
      toast.success(rule ? t("Earn rule saved") : t("Earn rule created"));
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("SomethingWentWrongDescriptionWithSupport"),
      );
    }
  };

  const handleToggle = async () => {
    if (!rule) return;
    try {
      await setEnabled({ variables: { id: rule.id, enabled: !rule.enabled } });
      toast.success(
        rule.enabled ? t("Earn rule paused") : t("Earn rule resumed"),
      );
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("SomethingWentWrongDescriptionWithSupport"),
      );
    }
  };

  const busy = saving || toggling;

  return (
    <Drawer
      open={open}
      onClose={() => !busy && onClose()}
      title={rule ? t("Edit earn rule") : t("New earn rule")}
      loading={busy}
      footer={
        <Inline gap="var(--space-3)" justify="end">
          {rule && canEdit && (
            <Button
              variant="default-outlined"
              onClick={handleToggle}
              disabled={busy}
            >
              {rule.enabled ? t("Pause rule") : t("Resume rule")}
            </Button>
          )}
          <Button variant="primary-outlined" onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </Button>
          {canEdit && (
            <Button
              variant="primary-filled"
              onClick={handleSave}
              disabled={busy || !complete}
            >
              {t("Save rule")}
            </Button>
          )}
        </Inline>
      }
    >
      <Stack gap="var(--space-5)">
        <TextField
          label={t("Rule name")}
          required
          fullWidth
          value={form.name}
          onChange={(event) => set("name", event.target.value)}
          disabled={!canEdit}
        />
        <FormField label={t("Earn on")} required asFieldset fullWidth>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={form.scope}
            onChange={(_, next) =>
              next && set("scope", next as RewardRuleScope)
            }
            aria-label={t("Earn on")}
            disabled={!canEdit}
          >
            <ToggleButton value="ALL">{t("All merchants")}</ToggleButton>
            <ToggleButton value="CATEGORY">{t("Category")}</ToggleButton>
            <ToggleButton value="MERCHANT">{t("Merchant")}</ToggleButton>
          </ToggleButtonGroup>
        </FormField>
        {form.scope === "CATEGORY" && (
          <Select
            label={t("Merchant category")}
            required
            options={categories.map((category) => ({
              id: category.id,
              label: category.name,
            }))}
            value={form.defaultCategoryId}
            onChange={(id) => set("defaultCategoryId", id)}
            searchable
            disabled={!canEdit}
          />
        )}
        {form.scope === "MERCHANT" && (
          <Select
            label={t("Merchant")}
            required
            searchable
            options={merchants.options}
            loading={merchants.loading}
            onInputChange={merchants.onInputChange}
            highlight={merchants.query}
            noOptionsText={t("Type a merchant name to search")}
            value={form.merchant?.id ?? null}
            onChange={(id) =>
              set("merchant", id ? (merchants.optionById(id) ?? null) : null)
            }
            disabled={!canEdit}
          />
        )}
        <Columns gap="var(--space-4)">
          <TextField
            label={t("EarnRatePercentLabel")}
            required
            type="number"
            value={form.ratePercent}
            onChange={(event) => set("ratePercent", event.target.value)}
            disabled={!canEdit}
          />
          <TextField
            label={t("Priority")}
            required
            type="number"
            tooltip={t("RulePriorityTooltip")}
            errorText={
              priorityHolder
                ? t("RewardRulePriorityTaken", {
                    name: priorityHolder.name,
                    priority,
                  })
                : undefined
            }
            value={form.priority}
            onChange={(event) => set("priority", event.target.value)}
            disabled={!canEdit}
          />
        </Columns>
        <Select
          label={t("Applies to")}
          required
          clearable={false}
          options={[
            { id: ALL_COMPANIES, label: t("All accounts") },
            { id: SELECTED_COMPANIES, label: t("Selected accounts") },
          ]}
          value={form.companiesMode}
          onChange={(id) => id && set("companiesMode", id)}
          disabled={!canEdit}
        />
        {form.companiesMode === SELECTED_COMPANIES && (
          <Select
            multiple
            label={t("Accounts")}
            required
            searchable
            options={companyOptions}
            loading={companies.loading}
            onInputChange={companies.onInputChange}
            onScrollEnd={companies.onScrollEnd}
            highlight={companies.query}
            value={form.companies.map((company) => company.id)}
            onChange={(ids) =>
              set(
                "companies",
                ids
                  .map((id) =>
                    companyOptions.find((option) => option.id === id),
                  )
                  .filter((option): option is SelectOption => Boolean(option)),
              )
            }
            disabled={!canEdit}
          />
        )}
        <Card variant="outlined">
          <CardContent>
            <Stack gap="var(--space-4)">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.limited}
                    onChange={(event) => set("limited", event.target.checked)}
                    disabled={!canEdit}
                  />
                }
                label={t("Limited time promotion")}
              />
              {form.limited && (
                <Columns gap="var(--space-4)">
                  <FormField label={t("Starts")} required>
                    <DatePicker
                      aria-label={t("Starts")}
                      value={form.startsOn}
                      onChange={(value) => set("startsOn", value)}
                      disabled={!canEdit}
                    />
                  </FormField>
                  <FormField label={t("Ends")} required>
                    <DatePicker
                      aria-label={t("Ends")}
                      value={form.endsOn}
                      onChange={(value) => set("endsOn", value)}
                      minDate={form.startsOn ?? undefined}
                      disabled={!canEdit}
                    />
                  </FormField>
                </Columns>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack gap="var(--space-4)">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.capped}
                    onChange={(event) => set("capped", event.target.checked)}
                    disabled={!canEdit}
                  />
                }
                label={t("Cap what one account can earn per month")}
              />
              {form.capped && (
                <TextField
                  label={t("RewardCapLabel", {
                    currency: capCurrency.toUpperCase(),
                  })}
                  required
                  type="number"
                  helperText={t("RewardCapHelper")}
                  value={form.monthlyCap}
                  onChange={(event) => set("monthlyCap", event.target.value)}
                  disabled={!canEdit}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack gap="var(--space-2)">
              <Typography variant="overline" color="text.secondary">
                {t("Summary")}
              </Typography>
              <Typography variant="body2">{summary}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Drawer>
  );
};

export default RewardRuleDrawer;
